function getSelectedOptionsFromContainer(container) {
  const options = [];
  const selects = container.querySelectorAll('select');
  const radios = container.querySelectorAll('input[type="radio"]:checked');

  if (selects.length > 0) {
    selects.forEach(select => options.push(select.value));
  } else if (radios.length > 0) {
    radios.forEach(radio => options.push(radio.value));
  }

  return options;
}

if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this._abortController = null;
      this._unsubscribeVariant = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      this.variantSelects = this.querySelector('variant-selects');

      this._unsubscribeVariant = theme.pubsub?.subscribe(
        theme.pubsub.events.VARIANT_CHANGE,
        this._onVariantChange.bind(this)
      );
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._unsubscribeVariant?.();
    }

    _onVariantChange(data) {
      if (data.sectionId !== this.sectionId) return;
      this._applyVariant(data.variant);
    }

    _applyVariant(variant) {
      this._updateURL(variant);
      this._updateVariantInput(variant);
      this._updatePriceDisplay(variant);
      this._updateAddToCartButton(variant);
      this._updateSku(variant);
      this._updateInventory(variant);
      this._updateUnitPrice(variant);
      this._updateGalleryMedia(variant);
    }

    _updateGalleryMedia(variant) {
      const mediaId = variant?.featured_media?.id;
      if (!mediaId) return;

      const slider = document.getElementById(`ProductGalleryMedia-${this.sectionId}`);
      if (!slider || typeof slider.navigateToSlide !== 'function') return;

      const slide = slider.querySelector(`.product-gallery-media__slide[data-media-id="${mediaId}"]`);
      if (!slide) return;

      const index = parseInt(slide.dataset.index, 10);
      if (Number.isNaN(index)) return;

      slider.navigateToSlide(index);
    }

    _updateURL(variant) {
      if (!variant) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    _updateVariantInput(variant) {
      if (!variant) return;
      const variantInput = this.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = variant.id;
    }

    _updatePriceDisplay(variant) {
      if (!variant) return;

      const priceElement = this.querySelector('.price__amount');
      const compareElement = this.querySelector('.price__amount--compare');
      const priceWrapper = this.querySelector('.price');

      if (priceElement) {
        priceElement.textContent = theme.utils.formatMoney(variant.price);
      }

      if (priceWrapper) {
        priceWrapper.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
        priceWrapper.classList.toggle('price--sold-out', !variant.available);
      }

      if (compareElement) {
        if (variant.compare_at_price > variant.price) {
          compareElement.textContent = theme.utils.formatMoney(variant.compare_at_price);
          compareElement.parentElement.hidden = false;
        } else {
          compareElement.parentElement.hidden = true;
        }
      }
    }

    _updateAddToCartButton(variant) {
      const button = this.querySelector('.main-product__submit');
      const buttonText = this.querySelector('.main-product__submit-text');

      if (!button || !buttonText) return;

      if (!variant) {
        button.disabled = true;
        buttonText.textContent = theme.locales?.product?.unavailable;
        return;
      }

      if (variant.available) {
        button.disabled = false;
        buttonText.textContent = theme.locales?.product?.addToCart;
      } else {
        button.disabled = true;
        buttonText.textContent = theme.locales?.product?.soldOut;
      }
    }

    _updateSku(variant) {
      const skuBlock = this.querySelector('[data-sku]');
      if (!skuBlock) return;

      const skuValue = skuBlock.querySelector('.main-product__sku-value');
      const hasSku = variant?.sku && variant.sku.trim() !== '';

      skuBlock.hidden = !hasSku;
      if (skuValue) skuValue.textContent = hasSku ? variant.sku : '';
    }

    _updateInventory(variant) {
      const inventoryBlock = this.querySelector('[data-inventory]');
      if (!inventoryBlock || !variant) return;

      const mapScript = this.querySelector('[data-inventory-map]');
      if (!mapScript) return;

      let inventoryMap;
      try {
        inventoryMap = JSON.parse(mapScript.textContent);
      } catch (_) {
        return;
      }

      const data = inventoryMap[variant.id];
      if (!data) return;

      const threshold = parseInt(inventoryBlock.dataset.threshold, 10) || 10;
      const showCount = inventoryBlock.dataset.showCount === 'true';
      const { quantity, management, policy } = data;

      let status;
      if (!management) {
        status = 'in_stock';
      } else if (quantity > threshold) {
        status = 'in_stock';
      } else if (quantity > 0) {
        status = 'low_stock';
      } else if (policy === 'continue') {
        status = 'backorder';
      } else {
        status = 'out_of_stock';
      }

      const locales = theme.locales?.product?.inventory;
      let text;
      if (status === 'low_stock' && showCount) {
        text = (locales?.lowStockCount || '').replace('[count]', String(quantity));
      } else {
        const keyMap = { in_stock: 'inStock', low_stock: 'lowStock', backorder: 'backorder', out_of_stock: 'outOfStock' };
        text = locales?.[keyMap[status]] || '';
      }

      const statusSpan = inventoryBlock.querySelector('.main-product__inventory-status');
      if (statusSpan) {
        statusSpan.className = `main-product__inventory-status main-product__inventory-status--${status}`;
        statusSpan.textContent = text;
      }
    }

    _updateUnitPrice(variant) {
      const unitPriceBlock = this.querySelector('.price__unit-price');
      if (!unitPriceBlock || !variant) return;

      const mapScript = this.querySelector('[data-unit-price-map]');
      if (!mapScript) return;

      let unitPriceMap;
      try {
        unitPriceMap = JSON.parse(mapScript.textContent);
      } catch (_) {
        return;
      }

      const data = unitPriceMap[variant.id];
      const hasUnitPrice = data?.referenceUnit != null;

      unitPriceBlock.hidden = !hasUnitPrice;
      if (!hasUnitPrice) return;

      const valueElement = unitPriceBlock.querySelector('.price__unit-price-value');
      const referenceElement = unitPriceBlock.querySelector('.price__unit-price-reference');
      const unitElement = unitPriceBlock.querySelector('.price__unit-price-unit');

      if (valueElement) valueElement.textContent = theme.utils.formatMoney(data.unitPrice);
      if (referenceElement) {
        referenceElement.textContent = data.referenceValue;
        referenceElement.hidden = data.referenceValue === 1;
      }
      if (unitElement) unitElement.textContent = data.referenceUnit;
    }
  }

  customElements.define('product-info', ProductInfo);
}

if (!customElements.get('variant-selects')) {
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this._style = this.dataset.style || 'dropdown';
      this._abortController = null;
      this._variants = null;
      this._variantMap = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      this.addEventListener('change', this._onSelectChange.bind(this), { signal: this._abortController.signal });
      this._buildCache();
    }

    disconnectedCallback() {
      this._abortController?.abort();
    }

    _buildCache() {
      const variantData = this.querySelector('[data-variants]');
      if (!variantData) return;

      this._variants = JSON.parse(variantData.textContent);
      this._variantMap = new Map();

      this._variants.forEach(variant => {
        this._variantMap.set(variant.options.join('|'), variant);
      });
    }

    _onSelectChange(event) {
      if (event.target === this) return;

      if (this._style === 'buttons' && event.target.type === 'radio') {
        this._updateButtonSelection(event.target);
      }

      this._updateVariantStatuses();

      const selectedOptions = this._getSelectedOptions();
      const variant = this._getCurrentVariant(selectedOptions);

      theme.pubsub?.publish(theme.pubsub.events.VARIANT_CHANGE, {
        source: 'product-info',
        sectionId: this.dataset.sectionId,
        variant,
        selectedOptions,
      });
    }

    _updateButtonSelection(selectedInput) {
      const optionGroup = selectedInput.closest('.main-product__option');
      if (!optionGroup) return;

      optionGroup.querySelectorAll('.main-product__option-value').forEach(label => {
        label.classList.remove('main-product__option-value--selected');
      });

      selectedInput.closest('.main-product__option-value')?.classList.add('main-product__option-value--selected');
    }

    _updateVariantStatuses() {
      if (!this._variantMap || this._style !== 'buttons') return;
      this._updateButtonStates(this._getSelectedOptions());
    }

    _updateButtonStates(selectedOptions) {
      const optionGroups = this.querySelectorAll('.main-product__option');

      optionGroups.forEach((group, optionIndex) => {
        group.querySelectorAll('.main-product__option-value').forEach(button => {
          const input = button.querySelector('input[type="radio"]');
          if (!input) return;

          const testOptions = [...selectedOptions];
          testOptions[optionIndex] = input.value;

          const matchingVariant = this._variantMap.get(testOptions.join('|'));

          button.classList.toggle('main-product__option-value--disabled', !matchingVariant?.available);
          input.disabled = !matchingVariant;
        });
      });
    }

    _getCurrentVariant(selectedOptions) {
      if (!this._variantMap) return null;
      return this._variantMap.get(selectedOptions.join('|')) ?? null;
    }

    _getSelectedOptions() {
      return getSelectedOptionsFromContainer(this);
    }
  }

  customElements.define('variant-selects', VariantSelects);
}

if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="number"]');
      this.minusButton = this.querySelector('[name="minus"]');
      this.plusButton = this.querySelector('[name="plus"]');
      this.sectionId = this.dataset.section;
      this._abortController = null;
      this._unsubscribeVariant = null;
    }

    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;
      this.minusButton?.addEventListener('click', this._decrease.bind(this), { signal });
      this.plusButton?.addEventListener('click', this._increase.bind(this), { signal });
      this.input?.addEventListener('change', this._validateInput.bind(this), { signal });

      this._unsubscribeVariant = theme.pubsub?.subscribe(
        theme.pubsub.events.VARIANT_CHANGE,
        this._onVariantChange.bind(this)
      );
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._unsubscribeVariant?.();
    }

    _onVariantChange(data) {
      if (data.sectionId !== this.sectionId) return;
      if (!data.variant?.quantity_rule) return;

      const { min, max, increment } = data.variant.quantity_rule;
      const currentValue = parseInt(this.input?.value, 10) || min;

      if (this.input) {
        this.input.min = min;
        this.input.step = increment;
        if (max !== null) {
          this.input.max = max;
        } else {
          this.input.removeAttribute('max');
        }

        const clampedMax = max !== null ? max : Infinity;
        const clamped = Math.max(min, Math.min(clampedMax, currentValue));
        if (clamped !== currentValue) {
          this.input.value = clamped;
        }
      }
    }

    _decrease() {
      if (!this.input) return;

      const currentValue = parseInt(this.input.value, 10) || 1;
      const min = parseInt(this.input.min, 10) || 1;
      const step = parseInt(this.input.step, 10) || 1;

      if (currentValue > min) {
        this.input.value = Math.max(min, currentValue - step);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _increase() {
      if (!this.input) return;

      const currentValue = parseInt(this.input.value, 10) || 1;
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const step = parseInt(this.input.step, 10) || 1;

      if (currentValue < max) {
        this.input.value = max === Infinity ? currentValue + step : Math.min(max, currentValue + step);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _validateInput() {
      if (!this.input) return;

      let value = parseInt(this.input.value, 10);
      const min = parseInt(this.input.min, 10) || 1;
      const max = parseInt(this.input.max, 10) || Infinity;

      if (isNaN(value) || value < min) {
        value = min;
      } else if (value > max) {
        value = max;
      }

      this.input.value = value;
    }
  }

  customElements.define('quantity-input', QuantityInput);
}

function initShareDropdown(container) {
  const dropdown = container.querySelector('theme-dropdown[data-mode="menu"]');
  if (!dropdown) return;

  const trigger = dropdown.querySelector('[data-dropdown-trigger]');
  const copyButton = dropdown.querySelector('[data-copy-url]');
  const toast = container.querySelector('.main-product__share-toast');
  const shareUrl = window.location.href;

  if (trigger && 'share' in navigator) {
    trigger.addEventListener('click', (event) => {
      event.stopImmediatePropagation();
      navigator.share({ url: shareUrl, title: document.title }).catch((error) => {
        if (error.name !== 'AbortError') {
          dropdown.open();
        }
      });
    }, true);
  }

  if (copyButton) {
    copyButton.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch (_) {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.cssText = 'position:fixed;left:-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      copyButton.classList.add('copied');
      if (toast) {
        toast.hidden = false;
        setTimeout(() => { toast.hidden = true; }, 2000);
      }
      setTimeout(() => copyButton.classList.remove('copied'), 2000);
    });
  }
}

document.querySelectorAll('.main-product__share').forEach(initShareDropdown);
